<template>
  <SkeletonDataTable
    :id="id"
    :value="rateQuotes"
    :number-of-rows="rateQuotes.length"
    class="route-builder-rate-shop-table rate-response-table elevated"
    tabindex="-1"
    scrollable
  >
    <template v-if="slots.empty" #empty>
      <slot name="empty" />
    </template>

    <template #default>
      <Column :header="t('shipments.shipments')">
        <template #body="{ data }: DataTableRow">
          <Skeleton
            v-if="data.loading"
            width="88px"
            height="21px"
            class="bg-gray-darker align-self-center"
          />
          <template v-else-if="data.data?.result">
            <router-link
              :to="{
                name: 'shipmentDetail',
                params: { shipmentId: data.data.result.shipmentId },
              }"
            >
              {{
                t('shipments.shipmentNumParam', {
                  param: data.data.result.shipmentId,
                })
              }}
            </router-link>
          </template>
          <template v-else-if="data.data">
            {{ t('shipments.shipments') }}
            {{ data.data.shipmentIdentifier }}
          </template>
          <template v-else> - </template>
        </template>
      </Column>

      <Column :header="t('carriers.carriers')" field="rateQuote.carrier.scac">
        <template #body="{ data }: DataTableRow">
          <Skeleton
            v-if="data.loading"
            width="350px"
            height="42px"
            class="bg-gray-darker align-self-center"
          />
          <RateQuoteCarrier
            v-else
            :rate-quote="getRateQuote(data)"
            class="carrier-column-content"
          />
        </template>
      </Column>

      <Column :header="t('orders.origin')" field="shipment.origin">
        <template #body="{ data }: DataTableRow">
          <Skeleton
            v-if="data.loading"
            width="160px"
            height="42px"
            class="bg-gray-darker align-self-center"
          />
          <ShippingPointSearchResult
            v-else
            :shipping-point="getProposedShipment(data)?.origin"
          />
        </template>
      </Column>

      <Column :header="t('orders.destination')" field="shipment.destination">
        <template #body="{ data }: DataTableRow">
          <Skeleton
            v-if="data.loading"
            width="160px"
            height="42px"
            class="bg-gray-darker align-self-center"
          />
          <ShippingPointSearchResult
            v-else
            :shipping-point="getProposedShipment(data)?.destination"
          />
        </template>
      </Column>

      <Column :header="t('lineItems.lineItems', 2)" field="shipment.lineItems">
        <template #body="{ data }: DataTableRow">
          <Skeleton
            v-if="data.loading"
            width="80px"
            height="54x"
            class="bg-gray-darker align-self-center"
          />
          <LineItemSummarySearchResult
            v-else
            :line-items="getProposedShipment(data)?.lineItems"
            show-linear
          />
        </template>
      </Column>

      <Column :header="t('rateShop.transit')" field="rateQuote.transitTime">
        <template #body="{ data }: DataTableRow">
          <Skeleton
            v-if="data.loading"
            width="80px"
            height="21px"
            class="bg-gray-darker align-self-center"
          />
          <template v-else>
            {{
              data.data?.rateQuote?.transitTime
                ? t('rateShop.countDays', {
                    count: data.data.rateQuote.transitTime,
                  })
                : '-'
            }}
          </template>
        </template>
      </Column>

      <Column
        v-if="['preferred', 'none'].includes(type)"
        :header="t('routingGuides.rules')"
        field="routingGuideRule"
      >
        <template #body="{ data }: DataTableRow">
          <Skeleton
            v-if="data.loading"
            width="140px"
            height="21px"
            class="bg-gray-darker align-self-center"
          />
          <template v-else-if="data.data?.routingGuideRule">
            <a
              v-tooltip.top="{
                value: t('routingGuides.inactiveWarning'),
                disabled: data.data.routingGuideRule.active,
              }"
              :href="
                generateRoutingGuideLink(
                  data.data.routingGuideRule,
                  selectedDomain?.naturalId,
                )
              "
              class="routing-guide-link"
              :class="{
                'inactive line-through': !data.data.routingGuideRule.active,
              }"
            >
              {{ data.data.routingGuideRule.name }}
            </a>
          </template>
          <template v-else>-</template>
        </template>
      </Column>

      <Column
        :header="t('common.totalParam', { param: t('rateShop.price') })"
        field="rateQuote.totalPrice"
        class="price-column w-full text-right"
        align-frozen="right"
        frozen
      >
        <template #body="{ data }: DataTableRow">
          <RouteBuilderPhase2PriceColumn
            :shipment-request="data"
            :current-step="currentStep"
          />
        </template>
      </Column>
    </template>
  </SkeletonDataTable>
</template>

<script lang="ts" setup>
import { AxiosError } from 'axios';
import type { PropType } from 'vue';
import { toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useTooltip } from '@rygen/rygen-ui/composables';
import { ErrorResponse, Loadable } from '@rygen/rygen-ui/model';

import { RateQuoteGroupType } from '@/model/routeBuilder';
import {
  RouteBuilderPhase2Step,
  RoutePlanShipmentRequest,
} from '@/model/routeBuilder/phase2';
import { useCurrentUserStore } from '@/stores/CurrentUser';
import { generateRoutingGuideLink } from '@/util';

import RateQuoteCarrier from '@/components/rateShop/RateQuoteCarrier.vue';
import RouteBuilderPhase2PriceColumn from '@/components/routeBuilder/rateShop/RouteBuilderPhase2PriceColumn.vue';
import LineItemSummarySearchResult from '@/components/search/LineItemSummarySearchResult.vue';
import ShippingPointSearchResult from '@/components/search/ShippingPointSearchResult.vue';
import SkeletonDataTable from '@rygen/rygen-ui/components/ui/SkeletonDataTable.vue';
import Column from 'primevue/column';
import Skeleton from 'primevue/skeleton';

export type DataTableRow = {
  data: Loadable<RoutePlanShipmentRequest> & {
    error: AxiosError<ErrorResponse, any>;
  };
  index: number;
};

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  type: {
    type: String as PropType<RateQuoteGroupType>,
    required: true,
  },
  rateQuotes: {
    type: Array as PropType<Array<Loadable<RoutePlanShipmentRequest>>>,
    required: true,
  },
  currentStep: {
    type: String as PropType<RouteBuilderPhase2Step>,
    default: () => 'rating',
  },
});
const { id, type, rateQuotes, currentStep } = toRefs(props);

const slots = defineSlots<{
  empty?: () => any;
}>();

const { t } = useI18n();
const vTooltip = useTooltip();
const { selectedDomain } = useCurrentUserStore();

const getRateQuote = (data: DataTableRow['data']) => {
  return data.data?.rateQuote;
};

const getProposedShipment = (data: DataTableRow['data']) => {
  return data.data?.proposedShipment;
};
</script>
